import { get } from 'svelte/store';

import { apiClient } from '$lib/api/client';
import { selectedIndustryCentroids } from '$lib/stores/data';
import { queryFilters } from '$lib/stores/selection';
import { turnstile } from '$lib/turnstile';
import type { CompanyWithContacts, Contact } from '$lib/types/api';

/**
 * A selection is fetched in two parts.
 *
 * # Why not one
 *
 * A preview row is 827 bytes measured against the live corpus, so a broad
 * search — a contact role on its own matches 702,388 companies — is hundreds
 * of megabytes and simply never arrives.
 *
 * But the browser genuinely needs every identifier, and not for display:
 *
 *   - the order is a list of company and contact identifiers, so a customer
 *     who buys without scrolling to the bottom must still buy everything they
 *     were quoted for;
 *   - re-filtering a set that contains lookalike results can only be done by
 *     naming every company in it. Those came from a vector search around one
 *     company and cannot be described by the filters, so if the browser
 *     forgets them they vanish on the next filter change;
 *   - and the list is sorted in the browser by name, size and quality, which
 *     cannot be done to rows that have not been fetched.
 *
 * So the whole selection arrives as an index — identifier, name, size band,
 * score and the identifiers of its people, about 114 bytes a company — and the
 * rows that describe them follow a block at a time.
 */

/** One company as the index describes it. */
export interface IndexEntry {
  id: string;
  name: string;
  size: string;
  quality: number;
  contact_ids?: string[];
}

export interface IndexResponse {
  companies: IndexEntry[];
  total: number;
  capped: boolean;
}

export interface Selection {
  /** The whole selection, as skeleton rows in canonical order. */
  companies: CompanyWithContacts[];

  /** How many companies matched, which is more than `companies` holds when capped. */
  total: number;

  /** Whether the server trimmed the selection to what one list can hold. */
  capped: boolean;
}

/**
 * How many companies one block of rows covers.
 *
 * The server refuses a block larger than 500 — see maxPreviewBlock — and the
 * list renders 100 at a time, so a block covers a page and a little more. Small
 * enough that a block is imperceptible, large enough that scrolling does not
 * spend the client's rate-limit budget: the service allows one request a second
 * with a burst of three.
 */
export const blockSize = 100;

/**
 * A company the index named but whose row has not been fetched.
 *
 * Everything the list sorts by is present; everything the row shows is not.
 * `hydrated` is what tells the two apart — a company with no offerings is
 * indistinguishable from one whose offerings have not arrived.
 */
export function skeleton(entry: IndexEntry): CompanyWithContacts {
  return {
    id: entry.id,
    name: entry.name,
    size: entry.size,
    quality: entry.quality,
    hydrated: false,

    // Identifier-only contacts. The order is built from these, so they must be
    // here from the start; their names and addresses arrive with the row.
    contacts: (entry.contact_ids ?? []).map((id): Contact => ({
      id,
      company_id: entry.id,
      name: '',
      role: ''
    }))
  };
}

/**
 * Fetch the whole selection as an index.
 *
 * `within` narrows a set the caller already holds rather than searching afresh,
 * which is the only way to re-filter one containing lookalike results.
 */
export async function fetchSelection(within?: string[]): Promise<Selection> {
  const filters = get(queryFilters);
  const centroids = Array.from(get(selectedIndustryCentroids).keys()).map(Number);

  let turnstileToken: string | undefined;
  try {
    turnstileToken = await turnstile.getToken();
  } catch {
    // Continue without one; the server decides whether it was required.
  }

  const response = await apiClient.post<IndexResponse>('/deep-iq/ids', {
    filters,
    data_pool_ids: centroids,
    contact_mode: filters.contact_mode || 'single',
    ...(within?.length ? { company_ids: within } : {}),
    turnstile_token: turnstileToken
  });

  const { companies, total, capped } = response.data;

  return {
    companies: (companies ?? []).map(skeleton),
    total: total ?? 0,
    capped: capped ?? false
  };
}

/**
 * Fetch the rows for one block of companies.
 *
 * This is a fetch, not a search: no filters travel with it, so a block cannot
 * come back short and the list cannot develop a hole.
 */
export async function fetchBlock(companyIds: string[]): Promise<CompanyWithContacts[]> {
  if (companyIds.length === 0) return [];

  const filters = get(queryFilters);

  const response = await apiClient.post<{ companies: CompanyWithContacts[]; contacts: Contact[] }>(
    '/deep-iq/preview',
    {
      company_ids: companyIds,
      contact_roles: filters.contact_roles ?? [],
      // A block is fetched on its own and has to know which person represents
      // each company. Sending only the roles left a title selection with rows
      // and nobody in them.
      contact_titles: filters.contact_titles ?? [],
      contact_mode: filters.contact_mode || 'single'
    }
  );

  const { companies = [], contacts = [] } = response.data;

  const byCompany = contacts.reduce<Record<string, Contact[]>>((acc, contact) => {
    (acc[contact.company_id] ||= []).push(contact);
    return acc;
  }, {});

  return companies.map((company) => ({
    ...company,
    hydrated: true,
    contacts: byCompany[company.id] ?? []
  }));
}
